import { Fragment, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, TrendingUp, Trophy, Target, Users,
  ChevronDown, ChevronRight, IndianRupee,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

// ==================== Types ====================
// 2026-10-08 — counts come from the scorecard / target engine (backend
// incentive-counts.ts). Anything with no rule or target is null → "Not set up".

interface CountedItem {
  id: string;
  name: string | null;
  phone: string | null;
  stage: string | null;
  date: string | null;
  how?: string;
}

interface TargetLine {
  productGroup: string;
  label: string;
  unit: 'L' | '₹';
  target: number;
  achieved: number;
  attainmentPct: number;
  overTargetRate: string | null;
  overTargetBonus: number | null;
}

interface UserIncentive {
  userId: string;
  userName: string;
  email: string | null;
  qualifiedLeads: number;
  meetingsFixed: number;
  dealsWon: number;
  /** null = no per-activity incentive set up (Manak 2026-10-08). */
  leadIncentive: number | null;
  meetingIncentive: number | null;
  closureIncentive: number | null;
  bonusAmount: number | null;
  /** The configured over-target bonus only; null = nothing set up. */
  grandTotal: number | null;
  targetAchievement: number | null;
  status: 'Exceeded' | 'On Track' | 'Behind' | null;
  bonusEligible: boolean;
  targets: TargetLine[];
  reportCard: string;
  qualifyingDeals: CountedItem[];
  meetingDealsList: CountedItem[];
  closedDealsList: CountedItem[];
}

interface TeamData {
  period: string;
  rates: {
    qualifiedLead: number | null;
    meetingFixed: number | null;
    closure: number | null;
  };
  team: UserIncentive[];
  summary: {
    totalTeamIncentive: number | null;
    totalClosures: number;
    avgTargetAchievement: number | null;
    repsWithTarget?: number;
    bestPerformer: { userId: string; name: string; total: number } | null;
    teamSize: number;
  };
}

const NOT_SET_UP = 'Not set up';
const money = (v: number | null | undefined) => (v == null ? NOT_SET_UP : formatINR(v));

// ==================== Helpers ====================

function getTargetColor(pct: number): string {
  if (pct >= 100) return 'bg-green-100 text-green-800';
  if (pct >= 75) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

function getTargetBg(pct: number): string {
  if (pct >= 100) return 'bg-green-500';
  if (pct >= 75) return 'bg-yellow-500';
  return 'bg-red-500';
}

const num = (n: number) => Math.round(n).toLocaleString('en-IN');

function targetText(t: TargetLine): string {
  return t.unit === 'L'
    ? `${t.label} ${num(t.achieved)} / ${num(t.target)} L`
    : `${t.label} ${formatINR(t.achieved)} / ${formatINR(t.target)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "3 Oct 2026" in India time. */
function istDate(isoStr: string | null): string {
  if (!isoStr) return '-';
  const d = new Date(new Date(isoStr).getTime() + 5.5 * 60 * 60 * 1000);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  // Last 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}

function CountedTable({ title, rows, dateLabel, showHow }: { title: string; rows: CountedItem[]; dateLabel: string; showHow?: boolean }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">{title} ({rows.length})</h4>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white max-sm:overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-3 py-2 text-left text-gray-500">Name</th>
              <th className="px-3 py-2 text-left text-gray-500">Phone</th>
              <th className="px-3 py-2 text-left text-gray-500">Stage</th>
              <th className="px-3 py-2 text-left text-gray-500">{dateLabel}</th>
              {showHow && <th className="px-3 py-2 text-left text-gray-500">Counted as</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2 font-medium text-gray-800 max-sm:min-w-[140px]">{d.name || '-'}</td>
                <td className="px-3 py-2 text-gray-600">{d.phone || '-'}</td>
                <td className="px-3 py-2">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 capitalize text-gray-700">{d.stage || '-'}</span>
                </td>
                <td className="px-3 py-2 text-gray-600">{istDate(d.date)}</td>
                {showHow && <td className="px-3 py-2 text-gray-600">{d.how || '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== Component ====================

export function IncentivesPage() {
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/incentives/team?month=${month}`);
      setData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load incentives');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRow = (userId: string) => {
    setExpandedRow(prev => (prev === userId ? null : userId));
  };

  const monthOptions = getMonthOptions();

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between max-sm:flex-wrap max-sm:gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Incentives</h1>
          <p className="text-sm text-gray-500">Track sales team performance and incentive payouts</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 max-sm:min-h-[40px] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading incentives...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline max-sm:min-h-[40px]">Retry</button>
        </div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 max-sm:grid-cols-2 max-sm:gap-3">
            {/* Total Team Incentive */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <IndianRupee size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Team Incentive</p>
                  <p className="text-xl font-bold text-gray-900">{money(data.summary.totalTeamIncentive)}</p>
                </div>
              </div>
            </div>

            {/* Best Performer */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-100">
                  <Trophy size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Best Performer</p>
                  <p className="text-lg font-bold text-gray-900">
                    {data.summary.bestPerformer?.name ?? 'N/A'}
                  </p>
                  {data.summary.bestPerformer && (
                    <p className="text-xs text-gray-400">{formatINR(data.summary.bestPerformer.total)}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Avg Target Achievement */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Target size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Avg Target Achievement</p>
                  <p className="text-xl font-bold text-gray-900">
                    {data.summary.avgTargetAchievement == null ? NOT_SET_UP : `${data.summary.avgTargetAchievement}%`}
                  </p>
                  {data.summary.repsWithTarget != null && (
                    <p className="text-xs text-gray-400">{data.summary.repsWithTarget} of {data.summary.teamSize} reps have a target</p>
                  )}
                </div>
              </div>
            </div>

            {/* Total Closures */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <TrendingUp size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Closures</p>
                  <p className="text-xl font-bold text-gray-900">{data.summary.totalClosures}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Incentive Rate Reference */}
          <div className="mb-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Qualified lead: {money(data.rates.qualifiedLead)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Meeting held: {money(data.rates.meetingFixed)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Closure (won lead): {money(data.rates.closure)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Over-target bonus: the only live reward — on a target line that has a bonus rate set, otherwise {NOT_SET_UP}
            </span>
          </div>

          {/* Team Table */}
          {data.team.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16 text-gray-400">
              <Users size={40} className="mb-2" />
              <p className="text-sm">No sales team data for this period</p>
              <p className="text-xs">Sales team members appear here with their leads, meetings and wins</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 w-8 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-9 max-sm:min-w-[36px] max-sm:bg-gray-50 max-sm:px-2.5" />
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 max-sm:sticky max-sm:left-9 max-sm:z-10 max-sm:bg-gray-50 max-sm:pl-1 max-sm:shadow-[1px_0_0_#e5e7eb]">Name</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-center">Qualified leads</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-center">Meetings held</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-center">Closures (won)</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-right">Over-target bonus</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-right">Total ₹</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500">Target (this month)</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-center">Target % · Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.team.map((member) => (
                    <Fragment key={member.userId}>
                      <tr
                        className="border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                        title="Open report card"
                        onClick={() => navigate(member.reportCard)}
                      >
                        <td className="px-3 py-3 text-gray-400 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-9 max-sm:min-w-[36px] max-sm:bg-white max-sm:px-2.5">
                          <button
                            type="button"
                            aria-label="Show counted items"
                            title="Show counted items"
                            className="rounded p-1 hover:bg-gray-200"
                            onClick={(e) => { e.stopPropagation(); toggleRow(member.userId); }}
                          >
                            {expandedRow === member.userId ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-3 max-sm:sticky max-sm:left-9 max-sm:z-10 max-sm:bg-white max-sm:pl-1 max-sm:shadow-[1px_0_0_#e5e7eb]">
                          <div className="max-sm:w-[128px]">
                            <p className="whitespace-nowrap font-medium text-[var(--ink-0)] underline-offset-2 hover:underline max-sm:truncate" title={member.email ?? undefined}>{member.userName}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <p className="font-medium text-gray-800">{member.qualifiedLeads}</p>
                          <p className="text-xs text-gray-400">{money(member.leadIncentive)}</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <p className="font-medium text-gray-800">{member.meetingsFixed}</p>
                          <p className="text-xs text-gray-400">{money(member.meetingIncentive)}</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <p className="font-medium text-gray-800">{member.dealsWon}</p>
                          <p className="text-xs text-gray-400">{money(member.closureIncentive)}</p>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">
                          {member.bonusAmount == null ? <span className="text-xs text-gray-400">{NOT_SET_UP}</span> : formatINR(member.bonusAmount)}
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-gray-900">{member.grandTotal == null ? <span className="text-xs font-normal text-gray-400">{NOT_SET_UP}</span> : formatINR(member.grandTotal)}</td>
                        <td className="px-3 py-3 text-xs text-gray-700">
                          {member.targets.length === 0 ? (
                            <span className="text-gray-400">No target set</span>
                          ) : (
                            <div className="min-w-[150px] space-y-1">
                              {member.targets.map((t) => <div key={t.productGroup}>{targetText(t)}</div>)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {member.targetAchievement == null || member.status == null ? (
                            <span className="text-xs text-gray-400">{NOT_SET_UP}</span>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-14 rounded-full bg-gray-200 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${getTargetBg(member.targetAchievement)}`}
                                    style={{ width: `${Math.min(member.targetAchievement, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-gray-600">{Math.round(member.targetAchievement)}%</span>
                              </div>
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getTargetColor(member.targetAchievement)}`}>
                                {member.status}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                      {/* Expanded Row: what was counted */}
                      {expandedRow === member.userId && (
                        <tr key={`${member.userId}-detail`}>
                          <td colSpan={10} className="bg-gray-50 px-8 py-4 max-sm:p-0">
                            <div className="space-y-4 max-sm:sticky max-sm:left-0 max-sm:w-[calc(100vw-34px)] max-sm:p-3">
                              <CountedTable title="Qualified leads" rows={member.qualifyingDeals} dateLabel="Qualified on" showHow />
                              <CountedTable title="Meetings held" rows={member.meetingDealsList} dateLabel="Meeting on" />
                              <CountedTable title="Closures (won leads)" rows={member.closedDealsList} dateLabel="Won on" />
                              {member.targets.some((t) => t.overTargetRate) && (
                                <p className="text-xs text-gray-500">
                                  Over-target bonus: {member.targets.filter((t) => t.overTargetRate).map((t) => `${t.label} — ${t.overTargetRate}`).join(' · ')}
                                </p>
                              )}
                              {member.qualifyingDeals.length === 0 &&
                                member.meetingDealsList.length === 0 &&
                                member.closedDealsList.length === 0 && (
                                  <p className="text-xs text-gray-400">No qualifying activity in this period</p>
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
